import React, {useMemo, useCallback, useEffect, ReactNode} from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import {FixedSizeList as List} from 'react-window';
import {ArrowRight, ArrowLeft} from '@carbon/icons-react';
import './pagination.scss'; 
import {TextInput} from '@carbon/react';
import Select from '../select';

const PAGE_SIZE_OPTIONS = [
    {text: '15', value: 15},
    {text: '20', value: 20},
    {text: '50', value: 50},
    {text: '100', value: 100},
    {text: '200', value: 200},
];

interface PaginationProps {
  totalItems: number;
  page: number;
  pageSize: number;
  disablePageSizeSelector?: boolean;
  pageSelectorType?: 'Dropdown' | 'Input';
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

const CustomPagination: React.FC<PaginationProps> = ({
    totalItems,
    page,
    pageSize,
    disablePageSizeSelector = false,
    pageSelectorType = 'Dropdown',
    onPageChange,
    onPageSizeChange
}) => {
    const totalPages = useMemo(
        () => Math.ceil(totalItems / pageSize),
        [totalItems, pageSize]
    );

    const [inputValue, setInputValue] = React.useState<string>((page + 1).toString());

    const pageRangeText = useMemo(
        () => `of ${totalPages} ${totalPages === 1 ? 'page' : 'pages'}`,
        [totalPages]
    );

    const handlePageChange = useCallback(
        (newPage: number) => {
            onPageChange(newPage);
        },
        [onPageChange, pageSize]
    );

    const handlePageSizeChange = useCallback(
        (value: string) => {
            onPageSizeChange(Number(value));
        },
        [onPageSizeChange]
    );

    const handlePageSelectChange = useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>) => {
            handlePageChange(Number(event.target.value));
        },
        [handlePageChange]
    );

    const handlePreviousPage = () => {
        if (page > 0) {
            handlePageChange(page - 1);
        }
    };

    const handleNextPage = () => {
        if (page < totalPages) {
            handlePageChange(page + 1);
        }
    };

    // Validate and set page input value
    const validateAndSetPage = useCallback(
        (value: string) => {
            const newPage = Number(value);
            if (newPage >= 1 && newPage <= totalPages) {
                handlePageChange(newPage - 1); // Convert to 0-based
            } else {
                // Reset to current page if invalid
                setInputValue((page + 1).toString());
            }
        },
        [handlePageChange, totalPages, page]
    );

    const handlePageInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setInputValue(event.target.value);
        },
        []
    );

    const handlePageInputBlur = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            validateAndSetPage(event.target.value);
        },
        [validateAndSetPage]
    );

    const handlePageInputKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                validateAndSetPage((event.target as HTMLInputElement).value);
            }
        },
        [validateAndSetPage]
    );

    const pageSizeSelector = useMemo(() => {
        const data = PAGE_SIZE_OPTIONS;
        const onChange = (value: string) => {
            handlePageSizeChange(value);
        };

        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            // Return the value as string for identification
            (item: any): string => {
                return item.value.toString();
            },
            // Render the display content
            (item: any): ReactNode => {
                return (
                    <span>{item.text}</span>
                );
            }
        ];

        return {
            label: '',
            data,
            value: pageSize.toString() ?? data[0].value.toString(),
            onChange,
            children,
            spinner: false,
            disabled: disablePageSizeSelector,
        };
    }, [pageSize, disablePageSizeSelector, handlePageSizeChange]);
    

    // Update input value when page changes externally
    useEffect(() => {
        setInputValue((page + 1).toString());
    }, [page]);

    const PageDropdown = useMemo(() => {
        const items = Array.from({length: totalPages}, (_, index) => ({
            value: index,
            text: index + 1,
        }));
        return (
            <select
                id="pageSelect"
                value={page}
                onChange={handlePageSelectChange}
                className="border border-gray-300 rounded px-2 py-1"
                data-testid="page-dropdown"
            >
                {items.map((item) => (
                    <option key={item.value} value={item.value} data-testid={`page-option-${item.text}`}>
                        {item.text}
                    </option>
                ))}
            </select>
        );
    }, [totalPages, page, handlePageSelectChange]);

    return (
        <div className="pagination-container flex justify-between items-center bg-white" data-testid="pagination-container">
            <div className="pagination-left flex items-center gap-2">
                <div className='page-size-slect-container'>
                    <Select
                        data={pageSizeSelector.data}
                        value={pageSizeSelector.value}
                        onChange={pageSizeSelector.onChange}
                        disabled={pageSizeSelector.disabled || totalItems === 0}
                        rightAlign
                        data-testid="page-size-select"
                        itemToString={(item: any) => item.text}
                    >
                        {pageSizeSelector.children}
                    </Select>
                </div>
                <span className="ml-2 record-count-text" data-testid="record-count-text">
                    {totalItems === 0
                        ? '0–0 '
                        : `${page * pageSize + 1}–${Math.min((page + 1) * pageSize, totalItems)} `}
                    of {totalItems} records
                </span>
            </div>
            <div className="pagination-right flex items-center gap-2" data-testid="pagination-controls">
                {
                    pageSelectorType === 'Dropdown' ?
                        <div data-testid="page-dropdown-container">
                            <label htmlFor="pageSelect"></label>
                            {PageDropdown}
                        </div> :
                        <div className='text-input-wrapper' data-testid="page-input-container">
                            <TextInput
                                id="pageSelect"
                                labelText="Go to page"
                                value={inputValue}
                                aria-label='Select Page'
                                onChange={handlePageInputChange}
                                onBlur={handlePageInputBlur}
                                onKeyDown={handlePageInputKeyDown}
                                type="number"
                                min={1}
                                max={totalPages}
                                data-testid="page-input"
                                disabled={totalItems === 0}
                            />
                        </div>
                }
                <div className="pagination-list" data-testid="pagination-list">
                    <AutoSizer disableHeight>
                        {({width}: any) => (
                            <List
                                height={35 * 10} 
                                itemCount={totalPages}
                                itemSize={20} 
                                width={width}
                            >
                                {({index}) => (
                                    <button
                                        key={index}
                                        onClick={() => handlePageChange(index + 1)}
                                        className={`pagination-button border border-gray-300 rounded px-2 py-1 ${
                                            index + 1 === page ? 'active bg-blue-500 text-white' : ''
                                        }`}
                                        disabled={index === page}
                                        data-testid={`page-button-${index + 1}`}
                                    >
                                        {index + 1}
                                    </button>
                                )}
                            </List>
                        )}
                    </AutoSizer>
                </div>
                <span data-testid="page-range-text">{pageRangeText}</span>
                <div className="pagination-icons flex items-center gap-1">
                    <button
                        onClick={handlePreviousPage}
                        className="cds--pagination__button cds--pagination__button--backward cds--btn cds--btn--ghost cds--btn--icon-only"
                        disabled={page <= 0}
                        aria-label="Previous page"
                        data-testid="previous-page-button"
                    >
                        <ArrowLeft size={16} />
                    </button>
                    <button
                        onClick={handleNextPage}
                        className="cds--pagination__button cds--pagination__button--forward cds--btn cds--btn--ghost cds--btn--icon-only"
                        disabled={page >= totalPages - 1}
                        aria-label="Next page"
                        data-testid="next-page-button"
                    >
                        <ArrowRight size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CustomPagination;
